import React, { useState } from 'react';
import classnames from 'classnames';

import style from './MeatballButton.module.scss';

import { Icon, IconType } from '@components/Icon';
import { ClickOutsideMask } from '@components/ClickOutsideMask';
import { SelectListNew } from '@components/SelectListNew';
import { CommonTestAttributes } from 'chaika-test-attributes';
import type { TranslationValues } from '@chaika/i18n/types';
import { translate } from '@chaika/i18n/helper';

export enum MeatballThemes {
    // @ts-ignore
    Primary = style.root_primary,
    // @ts-ignore
    Secondary = style.root_secondary,
    // @ts-ignore
    Light = style.root_light,
}

export enum MeatballModifier {
    // @ts-ignore
    Bigger = style.root_bigger,
    // @ts-ignore
    InheritSize = style.root_inheritSize,
    // @ts-ignore
    Tiny = style.root_tiny,
    // @ts-ignore
    Bordered = style.root_bordered,
    // @ts-ignore
    SquaredRadius = style.squaredRadius,
    // @ts-ignore
    CircledRadius = style.circledRadius,
    // @ts-ignore
    Telegram = style.telegram,
}

export enum MeatballMenuDirection {
    // @ts-ignore
    Bottom = style.menuWrapper,
    // @ts-ignore
    Top = style.menuWrapperTop,
    // @ts-ignore
    Right = style.menuWrapperRight,
}

export enum MeatballButtonLocation {
    Left = 'left',
    Right = 'right',
}

export interface MeatballButtonProps {
    theme?: MeatballThemes;
    customIcon?: IconType;
    modifiers?: MeatballModifier[];
    items: MeatballButtonMenuItem[];
    label?: TranslationValues;
    buttonLocation?: MeatballButtonLocation;
    dataTest?: string;
    withIcon?: boolean;
    direction?: MeatballMenuDirection;
    disabled?: boolean;
    onItemClick?: (value: string) => void | Promise<void>;
    zIndex?: number;
    listMaxWidth?: number;
}

export type LabelType = TranslationValues | string | React.ReactElement<any>;

export interface MeatballButtonMenuItemWithLabel {
    label: LabelType;
    shortLabel?: LabelType;
    dataTest?: string;
    isHighlighted?: boolean;
    disable?: boolean;
    value?: string;
    disableHint?: string;
    items?: MeatballButtonMenuItem[];
    onClick?(value?: string, event?: React.MouseEvent<HTMLDivElement>): void;
}
interface MeatballButtonMenuDivider {
    divider: true;
}

export type MeatballButtonMenuItem = MeatballButtonMenuItemWithLabel | MeatballButtonMenuDivider;

export function MeatballButton({
    theme,
    modifiers = [],
    items,
    zIndex,
    label,
    customIcon,
    buttonLocation = MeatballButtonLocation.Left,
    withIcon = false,
    direction = MeatballMenuDirection.Bottom,
    dataTest = CommonTestAttributes.ActionButton,
    onItemClick,
    listMaxWidth,
    disabled = false,
}: MeatballButtonProps) {
    const [isOpen, setIsOpen] = useState(false);

    function handleToggleIsOpen(event?: React.MouseEvent<HTMLDivElement>) {
        event?.stopPropagation();

        setIsOpen(prev => !prev);
    }

    return (
        <div
            className={classnames([
                style.root,
                isOpen && style.open,
                disabled && style.disabled,
                !!theme && theme,
                label && style.root_withLabel,
                ...modifiers,
            ])}
            data-test={dataTest}
            data-testid={dataTest}
            onClick={disabled ? undefined : handleToggleIsOpen}
        >
            {buttonLocation === MeatballButtonLocation.Left && (withIcon || customIcon) && (
                <Icon type={customIcon ?? IconType.MEATBALL} />
            )}
            {label && <span className={style.label}>{translate(label)}</span>}
            {isOpen && (
                <ClickOutsideMask
                    transparentBackground={true}
                    zIndex={zIndex}
                    onClickOutside={handleToggleIsOpen}
                />
            )}
            {buttonLocation === MeatballButtonLocation.Right && (withIcon || customIcon) && (
                <Icon type={customIcon ?? IconType.MEATBALL} />
            )}
            <div className={classnames([direction, theme && `${style.menuWrapper}_${theme}`])}>
                <SelectListNew
                    items={items}
                    maxWidth={listMaxWidth}
                    onItemClick={onItemClick}
                    onClose={handleToggleIsOpen}
                />
            </div>
        </div>
    );
}
